import React from 'react';

import colorsVars from 'styles/colors.module.scss';

import { ITableColumnState } from 'hooks/useTableCurrentState';

import { ActivityCell } from 'components/common/Tables/Cells/ActivityCell/ActivityCell';
import { BasicDateCell } from 'components/common/Tables/Cells/BasicDateCell/BasicDateCell';
import { CriticalPathCell } from 'components/common/Tables/Cells/CriticalPathCell/CriticalPathCell';
import { CriticalityScoreCell } from 'components/common/Tables/Cells/CriticalityScoreCell/CriticalityScoreCell';
import { DateCell } from 'components/common/Tables/Cells/DateCell/DateCell';
import { DetailsCell } from 'components/common/Tables/Cells/DetailsCell/DetailsCell';
import { DurationCell } from 'components/common/Tables/Cells/DurationCell/DurationCell';
import { FloatCell } from 'components/common/Tables/Cells/FloatCell/FloatCell';
import { PredecessorsCell } from 'components/common/Tables/Cells/PredecessorsCell/PredecessorsCell';
import { StatusCell } from 'components/common/Tables/Cells/StatusCell/StatusCell';
import { SuccessorsCell } from 'components/common/Tables/Cells/SuccessorsCell/SuccessorsCell';
import { TooltipCellWithCopy } from 'components/common/Tables/Cells/TooltipCellWithCopy/TooltipCellWithCopy';
import { VarianceCell } from 'components/common/Tables/Cells/VarianceCell/VarianceCell';
import { customSortWithNullV8 } from 'components/common/Tables/Tables.utils';
import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';
import { TrackedUntrackedActionCellWrapper } from 'components/common/TrackedUntrackedAction/trackedUntrackedActionCellWrapper/trackedUntrackedActionCellWrapper';

import { VersionCompareQueryKeys } from 'components/Dashboards/Project/Components/VersionCompare/VersionCompare.consts';
import CircularProgressWithLabel from 'components/DesignSystem/CircularProgressWithLabel';
import { CustomizedTooltip } from 'components/common';

import { getStatusLabel } from 'utilitys/Status/Status.utils';

const { color6: orange, textColor } = colorsVars;

const invalidateKeys = VersionCompareQueryKeys;

export const initialState: ITableColumnState = {
    hiddenColumns: [
        'details',
        'predecessors',
        'successors',
        'activityType',
        'lateFinish',
        'lateStart',
        'plannedStartDate',
        'plannedFinishDate',
        'actualStartDate',
        'actualFinishDate',
        'plannedDuration',
        'actualDuration',
        'remainingDuration',
        'completedDuration',
        'durationOverrun',
        'projectWeekStart',
        'projectWeekFinish',
        'projectMonthStart',
        'projectMonthFinish',
        'parentName',
        'grandParentName',
    ],
    sortBy: [{ id: 'duration', desc: true }],
};

export const columnsV8 = [
    {
        header: 'Activity',
        accessorKey: 'latestVersion.activityName',
        ...columnsCommonConfig.activity,
        cell: ({ row, toggleAllRowsSelected }) => {
            const activityName = row.original.latestVersion?.activityName;
            const activityId = row.original.latestVersion?.activityId;
            const isVirtual = row.original.latestVersion?.cardBoardViewResponse?.isVirtual;
            const cardIndicators = row.original.latestVersion?.cardBoardViewResponse?.cardIndicators;
            const isTracked = row.original.latestVersion?.cardBoardViewResponse?.isTracked;
            const taskVersionHashCode = row.original.latestVersion?.cardBoardViewResponse?.taskVersionHashCode;
            const taskId = row.original.latestVersion?.cardBoardViewResponse?.id;
            const { toggleRowSelected } = row;

            return (
                <ActivityCell
                    activityName={activityName}
                    activityId={activityId}
                    taskId={taskId}
                    cardIndicators={cardIndicators}
                    isVirtual={isVirtual}
                    toggleRowSelected={toggleRowSelected}
                    toggleAllRowsSelected={toggleAllRowsSelected}
                    trackedUntrackedActionCell={
                        <TrackedUntrackedActionCellWrapper
                            isTracked={isTracked}
                            taskVersionHashCode={taskVersionHashCode}
                            invalidateQueriesKey={invalidateKeys}
                        />
                    }
                />
            );
        },
    },
    {
        header: 'Status',
        accessorKey: 'latestVersion.cardBoardViewResponse.status.label',
        ...columnsCommonConfig.status,
        cell: ({ row }) => {
            const assignee = row.original.latestVersion?.cardBoardViewResponse?.assignee;
            const isVirtual = row.original.latestVersion?.cardBoardViewResponse?.isVirtual;
            const taskId = row.original.latestVersion?.cardBoardViewResponse?.id;
            const cardType = row.original.latestVersion?.cardBoardViewResponse?.type.name;
            const category = row.original.latestVersion?.cardBoardViewResponse?.category;
            const isInterimChanged = row.original.latestVersion?.cardBoardViewResponse?.isInterimChanged;

            const isDeleted = !row.original.latestVersion;
            const previousStatus = isDeleted
                ? row.original.comparedVersion?.activityStatus
                : row.original.changeMap?.activityStatus;
            const wasStatus =
                row.original.changeMap === null && !isDeleted ? 'Not Found' : `Was: ${getStatusLabel(previousStatus)}`;

            const status = row.original.latestVersion?.cardBoardViewResponse?.status;
            const originalStatus = row.original.latestVersion?.cardBoardViewResponse?.originalStatus;

            return (
                <StatusCell
                    assignee={assignee}
                    status={status}
                    isVirtual={isVirtual}
                    taskId={taskId}
                    originalStatus={originalStatus}
                    invalidateQueriesKey={invalidateKeys}
                    wasStatus={wasStatus}
                    cardType={cardType}
                    category={category}
                    isInterimChanged={isInterimChanged}
                />
            );
        },
    },
    {
        header: 'Submitted Completion',
        ...columnsCommonConfig.submittedDurationComplete,
        accessorKey: 'latestVersion.cardBoardViewResponse.submittedDurationComplete',
        cell: ({ getValue }) => {
            return (
                <CustomizedTooltip
                    tooltipContent={`Submitted Completion: ${getValue()}%`}
                    triggerElement={<CircularProgressWithLabel value={Number(getValue())} />}
                />
            );
        },
    },
    {
        header: 'Activity Type',
        ...columnsCommonConfig.activityType,
        accessorKey: 'latestVersion.cardBoardViewResponse.taskActivityType',
        cell: ({ getValue }) => <TooltipCellWithCopy text={getValue()} />,
    },
    {
        header: 'Details/Last Comment',
        accessorKey: 'latestVersion.cardBoardViewResponse.details',
        ...columnsCommonConfig.details,
        cell: ({ row }) => {
            const { details, cardLastComment, id, isVirtual, isGenAiDetails } =
                row.original.latestVersion?.cardBoardViewResponse;
            return (
                <DetailsCell
                    details={details}
                    cardLastComment={cardLastComment}
                    id={id}
                    isVirtual={isVirtual}
                    invalidateQueriesKey={invalidateKeys}
                    isGenAiDetails={isGenAiDetails}
                />
            );
        },
    },
    {
        header: 'Start Date',
        accessorKey: 'latestVersion.startDate',
        ...columnsCommonConfig.startDate,
        cell: ({ row }) => {
            return (
                <DateCell
                    latestVersionDate={row.original.latestVersion?.startDate}
                    changeMapDate={row.original.changeMap?.startDate}
                    comparedVersion={row.original.comparedVersion?.startDate}
                    isChangeMap={row.original.changeMap}
                />
            );
        },
        sortingFn: customSortWithNullV8,
    },
    {
        header: 'Finish Date',
        accessorKey: 'latestVersion.finishDate',
        ...columnsCommonConfig.finishDate,
        cell: ({ row }) => {
            return (
                <DateCell
                    latestVersionDate={row.original.latestVersion?.finishDate}
                    changeMapDate={row.original.changeMap?.finishDate}
                    comparedVersion={row.original.comparedVersion?.finishDate}
                    isChangeMap={row.original.changeMap}
                />
            );
        },
        sortingFn: customSortWithNullV8,
    },
    {
        header: 'Duration',
        ...columnsCommonConfig.duration,
        accessorKey: 'latestVersion.duration',
        cell: ({ row }) => {
            return (
                <DurationCell
                    changeMap={row.original.changeMap}
                    changeMapDuration={row.original.changeMap?.duration}
                    latestVersionDuration={row.original.latestVersion?.duration}
                    comparedVersionDuration={row.original.comparedVersion?.duration}
                />
            );
        },
        sortingFn: customSortWithNullV8,
    },
    {
        header: 'Criticality Score',
        ...columnsCommonConfig.criticalityScore,
        accessorKey: 'latestVersion.criticalityScore',
        cell: ({ row }) => {
            return <CriticalityScoreCell row={row} />;
        },
        sortingFn: customSortWithNullV8,
    },
    {
        header: 'Float',
        ...columnsCommonConfig.float,
        accessorKey: 'latestVersion.float',
        cell: ({ row }) => {
            return <FloatCell row={row} />;
        },
        sortingFn: customSortWithNullV8,
    },
    {
        header: 'Critical Path',
        accessorFn: (row) =>
            row.latestVersion?.cardBoardViewResponse?.cardIndicators.isCriticalPath.isIndicatorOn ? 'Yes' : 'No',
        ...columnsCommonConfig.criticalPath,
        cell: ({ row, getValue }) => {
            return <CriticalPathCell row={row} value={getValue()} />;
        },
    },
    {
        header: 'Pred.',
        ...columnsCommonConfig.predecessors,
        accessorKey: 'latestVersion.predecessors',
        cell: ({ row }) => {
            return <PredecessorsCell row={row} />;
        },
        sortingFn: customSortWithNullV8,
    },
    {
        header: 'Succ.',
        ...columnsCommonConfig.successors,
        accessorKey: 'latestVersion.successors',
        cell: ({ row }) => {
            return <SuccessorsCell row={row} />;
        },
        sortingFn: customSortWithNullV8,
    },
    {
        header: 'Finish Variance',
        ...columnsCommonConfig.lateFinish,
        accessorKey: 'latestVersion.cardBoardViewResponse.finishVariance',
        cell: ({ getValue }) => {
            return <VarianceCell value={getValue()} />;
        },
    },
    {
        header: 'Start Variance',
        ...columnsCommonConfig.lateStart,
        accessorKey: 'latestVersion.cardBoardViewResponse.startVariance',
        cell: ({ getValue }) => {
            return <VarianceCell value={getValue()} />;
        },
    },
    {
        header: 'Planned Start Date ',
        ...columnsCommonConfig.plannedStartDate,
        accessorKey: 'latestVersion.cardBoardViewResponse.plannedStartDate',
        cell: ({ getValue }) => {
            return <BasicDateCell value={getValue()} />;
        },
    },
    {
        header: 'Planned Finish Date',
        ...columnsCommonConfig.plannedFinishDate,
        accessorKey: 'latestVersion.cardBoardViewResponse.plannedFinishDate',
        cell: ({ getValue }) => {
            return <BasicDateCell value={getValue()} />;
        },
    },
    {
        header: 'Actual Start Date',
        ...columnsCommonConfig.actualStartDate,
        accessorKey: 'latestVersion.cardBoardViewResponse.actualStartDate',
        cell: ({ getValue }) => {
            return <BasicDateCell value={getValue()} />;
        },
    },
    {
        header: 'Actual Finish Date',
        ...columnsCommonConfig.actualFinishDate,
        accessorKey: 'latestVersion.cardBoardViewResponse.actualFinishDate',
        cell: ({ getValue }) => {
            return <BasicDateCell value={getValue()} />;
        },
    },
    {
        header: 'Planned Duration',
        ...columnsCommonConfig.plannedDuration,
        accessorKey: 'latestVersion.cardBoardViewResponse.plannedDurationInDays',
    },
    {
        header: 'Actual Duration',
        ...columnsCommonConfig.actualDuration,
        accessorKey: 'latestVersion.cardBoardViewResponse.actualDurationInDays',
    },
    {
        header: 'Remaining Duration',
        ...columnsCommonConfig.remainingDuration,
        accessorKey: 'latestVersion.cardBoardViewResponse.remainingDurationInDays',
    },
    {
        header: 'Current Duration',
        ...columnsCommonConfig.completedDuration,
        accessorKey: 'latestVersion.cardBoardViewResponse.completedDurationInDays',
    },
    {
        header: 'Current Duration Overrun',
        ...columnsCommonConfig.durationOverrun,
        accessorKey: 'latestVersion.cardBoardViewResponse.completedDurationOverrun',
        cell: ({ getValue }) => {
            const color = getValue() > 0 ? orange : textColor;
            return <span style={{ color }}>{getValue()}</span>;
        },
    },
    {
        header: 'Project Week Start ',
        ...columnsCommonConfig.projectWeekStart,
        accessorKey: 'latestVersion.cardBoardViewResponse.projectWeekStart',
    },
    {
        header: 'Project Week Finish',
        ...columnsCommonConfig.projectWeekFinish,
        accessorKey: 'latestVersion.cardBoardViewResponse.projectWeekFinish',
    },
    {
        header: 'Project Month Start',
        ...columnsCommonConfig.projectMonthStart,
        accessorKey: 'latestVersion.cardBoardViewResponse.projectMonthStart',
    },
    {
        header: 'Project Month Finish',
        ...columnsCommonConfig.projectMonthFinish,
        accessorKey: 'latestVersion.cardBoardViewResponse.projectMonthFinish',
    },
    {
        header: 'Immediate Parent Name ',
        ...columnsCommonConfig.parentName,
        accessorKey: 'latestVersion.cardBoardViewResponse.parentActivity',
        cell: ({ getValue }) => <TooltipCellWithCopy text={getValue()} />,
    },
    {
        header: 'Grand Parent Name',
        ...columnsCommonConfig.grandParentName,
        accessorKey: 'latestVersion.cardBoardViewResponse.grandParentActivity',
        cell: ({ getValue }) => <TooltipCellWithCopy text={getValue()} />,
    },
];
